import React, { useEffect, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Backdrop,
  CircularProgress,
  Typography,
  styled,
} from "@mui/material"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useParams } from "react-router-dom"
import AdminAccessUserConfigForm from "./AdminAccessUserConfigForm"
import {
  clearAdminAccessMessages,
  enableAdminAccessSelectedUserBaseAccess,
  fetchAdminAccessRoles,
  refreshAdminAccessSelectedUserContext,
  revokeAdminAccessSelectedUserRole,
  setAdminAccessSelectedRoles,
  setAdminAccessSelectedUser,
  setAdminAccessUserFinishDate,
  submitAdminAccessSelectedUserRoles,
} from "../../../redux/actions/adminAccessUserActions"
import { isAccessGrantedAndActive } from "../../../utils/adminAccessUserHelpers"
import InfoIcon from "@mui/icons-material/Info"
import useStyles from "./useStyles"
import { getUserStatusCorpEntities } from "../../../app/services/Organization/getUserStatusCorpEntities"


const InputButton = styled(Button)(() => ({
  background: "#1C846F",
  "&:hover": {
    background: "#0CABA8",
  },
  borderRadius: "5rem",
  color: "#fff",
}))

export const AdminAccessUserConfigContent = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { userId: routeUserId } = useParams()
  const classes = useStyles()


  const {
    organization,
    orgAccess,
    applicationInstanceId,
    organizationUsers,
    selectedUser,
    userFinishDate,
    roles,
    grantedRoleCodes,
    selectedRoleCodes,
    loadingRoles,
    loadingUserContext,
    enablingUserAccess,
    submittingUserRoles,
    revokingUserRole,
    error,
    successMessage,
    userAccess,
    appCode,
    applicationsRoutes,
  } = useSelector((state) => state.adminAccessUser)
  const [loading, setLoading] = useState(false)
  const [statesUserOrg, setStatesUserOrg] = useState([])

  const currentUser =
    (selectedUser?.userId === routeUserId && selectedUser) ||
    organizationUsers?.find((item) => item?.userId === routeUserId) ||
    null

  const getUserOrgStatus = async () => {
    try {
      const status = await getUserStatusCorpEntities()
      setStatesUserOrg(status.data?.data)
    } catch (error) {
      console.log(error)
    }
  }

  useEffect(() => {
    if (currentUser && currentUser?.userId !== selectedUser?.userId) {
      dispatch(setAdminAccessSelectedUser(currentUser))
    }
  }, [dispatch, currentUser, selectedUser])

  useEffect(() => {
    dispatch(fetchAdminAccessRoles(appCode)).catch(() => null)
  }, [dispatch])

  useEffect(() => {
    if (currentUser?.userId && applicationInstanceId) {
      getUserOrgStatus()
      dispatch(
        refreshAdminAccessSelectedUserContext({
          userId: currentUser.userId,
          applicationInstanceId,
          appCode
        }),
      ).catch(() => null)
    }
  }, [dispatch, currentUser, applicationInstanceId])

  const handleBack = () => {
    navigate(`/admin-access-user-app-content/application${appCode}`)
  }

  const handleUserFinishDateChange = (value) => {
    dispatch(setAdminAccessUserFinishDate(value))
  }

  const handleToggleRole = (roleCode) => {
    const nextSelection = selectedRoleCodes.includes(roleCode)
      ? selectedRoleCodes.filter((item) => item !== roleCode)
      : [...selectedRoleCodes, roleCode]

    dispatch(setAdminAccessSelectedRoles(nextSelection))
  }

  const handleEnableUserAccess = async () => {
    setLoading(true)
    try {
      dispatch(clearAdminAccessMessages())
      await dispatch(enableAdminAccessSelectedUserBaseAccess())
    } catch (error) {
      console.log(error)
    }
    setLoading(false)
    return null
  }

  const handleGrantRoles = async () => {
    setLoading(true)
    try {
      dispatch(clearAdminAccessMessages())
      await dispatch(submitAdminAccessSelectedUserRoles())
    } catch (error) {
      console.log(error)
    }
    setLoading(false)
    return null
  }

  const handleRevokeRole = async (roleCode) => {
    setLoading(true)
    try {
      dispatch(clearAdminAccessMessages())
      await dispatch(revokeAdminAccessSelectedUserRole(roleCode))
    } catch (error) {
      console.log(error)
    }
    setLoading(false)
    return null
  }


  const applicationName = applicationsRoutes.find((app) => app.application_code === appCode)?.application_name ?? ""


  const hasOrganization = Boolean(organization?.organizationId)
  const hasOrgBaseAccess = isAccessGrantedAndActive(orgAccess)
  const hasUserBaseAccess = isAccessGrantedAndActive(userAccess)

  if (!hasOrganization) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Primero debes buscar una organización por CUIT y seleccionar un
          usuario desde la tabla.
        </Alert>
        <InputButton onClick={handleBack}>
          Volver
        </InputButton>
      </Box>
    )
  }

  if (!currentUser) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          No se encontró el usuario seleccionado dentro del contexto cargado.
        </Alert>

        <Button variant="contained" onClick={handleBack}>
          Volver
        </Button>
      </Box>
    )
  }

  if (!hasOrgBaseAccess || !applicationInstanceId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          La organización todavía no tiene acceso base activo a la aplicación. Debes
          habilitar primero la relación organización / aplicación.
        </Alert>

        <Button variant="contained" onClick={handleBack}>
          Volver
        </Button>
      </Box>
    )
  }

  return (
    <div className={classes.root}>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading || loadingRoles || loadingUserContext}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <div className={classes.boxtitle}>
        <Typography className={classes.title}>
          Configuración de acceso del usuario: {applicationName}
        </Typography>
      </div>
      <div className={classes.boxSubTitle}>
        <InfoIcon
          style={{ fontSize: "2rem", color: "#00a78f", marginLeft: 10 }}
        />
        <Typography className={classes.subtitle}>
          En esta sección podrá configurar el acceso del usuario a la
          aplicación.
        </Typography>
      </div>
      <div style={{ marginTop: "2%", marginLeft: "5%" }}>
        <AdminAccessUserConfigForm
          organization={organization}
          user={currentUser}
          userFinishDate={userFinishDate}
          roles={roles}
          grantedRoleCodes={grantedRoleCodes}
          selectedRoleCodes={selectedRoleCodes}
          loadingRoles={loadingRoles}
          loadingUserContext={loadingUserContext}
          enablingUserAccess={enablingUserAccess}
          submittingUserRoles={submittingUserRoles}
          revokingUserRole={revokingUserRole}
          error={error}
          successMessage={successMessage}
          hasUserBaseAccess={hasUserBaseAccess}
          onUserFinishDateChange={handleUserFinishDateChange}
          onToggleRole={handleToggleRole}
          onEnableUserAccess={handleEnableUserAccess}
          onGrantRoles={handleGrantRoles}
          onRevokeRole={handleRevokeRole}
          userAccess={userAccess}
          applicationInstanceId={applicationInstanceId}
          states={statesUserOrg}
        />
      </div>
    </div>
  )
}

export default AdminAccessUserConfigContent
